import { CopyOutlined, EnvironmentOutlined, SyncOutlined } from '@ant-design/icons';
import { Button, Descriptions, Divider, Image, Modal, Spin, Table, Typography, message } from 'antd';
import { FC, useCallback, useEffect, useState } from 'react';

import { errorObjectToString, formatNumber } from 'src/utils/common';
import { callMaintainStore } from 'src/utils/firebase/firebase-callable';
import { getStore, getUser } from 'src/utils/firebase/firebase-short-cut';

interface StoreMenuItem {
  name: string;
  price: number | null;
  description: string | null;
  images: string[];
}

interface StoreDetailData {
  address: string;
  addressDetail: string;
  userTel: string;
  email: string;
  storeMenu: StoreMenuItem[];
}

function copyToClipboard(text: string, label: string): void {
  navigator.clipboard.writeText(text).then(() => {
    message.success(`${label}이(가) 복사되었습니다.`);
  });
}

const CopyableField: FC<{ value: string; label: string }> = ({ value, label }) => {
  if (!value) return <>-</>;
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      {value}
      <Button type='link' icon={<CopyOutlined />} size='small' onClick={() => copyToClipboard(value, label)}>
        복사
      </Button>
    </div>
  );
};

interface StoreDetailModalProps {
  open: boolean;
  storeName: string;
  storeId: string;
  onClose: () => void;
}

const StoreDetailModal: FC<StoreDetailModalProps> = ({ open, storeName, storeId, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [data, setData] = useState<StoreDetailData | null>(null);

  const fetchStoreDetail = useCallback(async (id: string) => {
    setLoading(true);
    setData(null);
    try {
      const store = await getStore(id);
      if (!store) {
        message.error('매장 정보를 찾을 수 없습니다.');
        return;
      }
      const address = store.roadAddress || store.jibunAddress || store.address || '';
      const addressDetail = store.addressDetail || '';
      const user = store.storeManagerId ? await getUser(store.storeManagerId) : null;
      setData({
        address,
        addressDetail,
        userTel: user?.userTel || '',
        email: user?.email || '',
        storeMenu: store.storeMenu ?? [],
      });
    } catch (error) {
      console.error('Failed to fetch store detail:', error);
      message.error('매장 정보 조회에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  }, []);

  const handleSyncMenu = useCallback(async () => {
    if (!storeId) return;
    setSyncing(true);
    try {
      await callMaintainStore({ storeId, operations: ['updateStoreMenu'] });
      message.success('메뉴 동기화 완료');
      await fetchStoreDetail(storeId);
    } catch (error) {
      message.error(`메뉴 동기화 실패: ${errorObjectToString(error)}`);
    } finally {
      setSyncing(false);
    }
  }, [storeId, fetchStoreDetail]);

  useEffect(() => {
    if (open && storeId) {
      fetchStoreDetail(storeId);
    }
  }, [open, storeId, fetchStoreDetail]);

  const naverMapUrl = data?.address
    ? `https://map.naver.com/v5/search/${encodeURIComponent(`${data.address} ${storeName}`)}`
    : undefined;

  const renderContent = () => {
    if (loading) {
      return (
        <div style={{ textAlign: 'center', padding: 24 }}>
          <Spin />
        </div>
      );
    }
    if (!data) return null;
    return (
      <>
        <Descriptions column={1} bordered size='small'>
          <Descriptions.Item label='주소'>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              {data.address}
              {naverMapUrl && (
                <Button type='link' icon={<EnvironmentOutlined />} size='small' href={naverMapUrl} target='_blank'>
                  지도
                </Button>
              )}
            </div>
          </Descriptions.Item>
          <Descriptions.Item label='상세 주소'>{data.addressDetail || '-'}</Descriptions.Item>
          <Descriptions.Item label='전화번호'>
            <CopyableField value={data.userTel} label='전화번호' />
          </Descriptions.Item>
          <Descriptions.Item label='이메일'>
            <CopyableField value={data.email} label='이메일' />
          </Descriptions.Item>
        </Descriptions>
        <Divider style={{ margin: '16px 0 12px' }} />
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <Typography.Title level={5} style={{ margin: 0 }}>
            매장 메뉴
          </Typography.Title>
          <Button size='small' icon={<SyncOutlined spin={syncing} />} loading={syncing} onClick={handleSyncMenu}>
            동기화
          </Button>
        </div>
        {data.storeMenu.length > 0 ? (
          <Table
            dataSource={data.storeMenu}
            rowKey={(_record, index) => String(index ?? 0)}
            size='small'
            pagination={false}
            scroll={{ y: 300 }}
            columns={[
              {
                title: '메뉴명',
                dataIndex: 'name',
                width: 140,
              },
              {
                title: '가격',
                dataIndex: 'price',
                width: 100,
                align: 'right',
                render: (price: number | null) => (price != null ? `${formatNumber(price, 0)}원` : '-'),
              },
              {
                title: '설명',
                dataIndex: 'description',
                ellipsis: true,
                render: (v: string | null) => v || '-',
              },
              {
                title: '이미지',
                dataIndex: 'images',
                width: 100,
                render: (images: string[]) =>
                  images?.length ? (
                    <Image.PreviewGroup>
                      <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                        {images.map((url, i) => (
                          <Image
                            key={i}
                            src={url}
                            width={32}
                            height={32}
                            style={{ objectFit: 'cover', borderRadius: 4, cursor: 'pointer' }}
                          />
                        ))}
                      </div>
                    </Image.PreviewGroup>
                  ) : (
                    '-'
                  ),
              },
            ]}
          />
        ) : (
          <Typography.Text type='secondary'>등록된 메뉴가 없습니다.</Typography.Text>
        )}
      </>
    );
  };

  return (
    <Modal title={storeName} open={open} onCancel={onClose} footer={null} width={800}>
      {renderContent()}
    </Modal>
  );
};

export default StoreDetailModal;
